import { useEffect, useState } from "react";
import { ArrowClockwise } from "@phosphor-icons/react";

/** ProductPicker — visual grid of every synced Printful product with search
 * + click-to-toggle. Returns an array of selected product IDs on save. */
export default function ProductPicker({ slotLabel, slotIndex, allSlotKeys, allProducts, initialIds, loading, onRefresh, onCancel, onSave }) {
  const [selected, setSelected] = useState(new Set(Array.from(initialIds || []).map(Number)));
  const [q, setQ] = useState("");

  // Keep local selection in sync when the parent swaps in a different slot
  // via "Save & next step" (see onSave callback in BundlesPanel).
  useEffect(() => {
    setSelected(new Set(Array.from(initialIds || []).map(Number)));
    setQ("");
  }, [initialIds]);

  const toggle = (pid) => {
    const next = new Set(selected);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    setSelected(next);
  };

  const filtered = allProducts.filter((p) => {
    if (!q) return true;
    return (p.name || "").toLowerCase().includes(q.toLowerCase());
  });

  const hasNext = Array.isArray(allSlotKeys) && slotIndex != null && allSlotKeys[slotIndex + 1];
  const totalSteps = Array.isArray(allSlotKeys) ? allSlotKeys.length : null;

  return (
    <div data-testid="admin-bundle-product-picker" role="dialog" className="fixed inset-0 z-[80] flex items-stretch md:items-center md:justify-center">
      <button type="button" aria-label="Close picker" onClick={onCancel} className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div className="relative w-full md:max-w-4xl md:max-h-[85vh] h-full md:h-auto bg-[#0A0A0A] border-2 border-[#FF1B91] overflow-y-auto shadow-2xl flex flex-col">
        <header className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10 p-4 flex items-center gap-3 flex-wrap">
          <div className="flex flex-col">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF1B91]">/ Pick eligible products</p>
            {slotLabel && (
              <p data-testid="admin-picker-slot-label" className="text-white text-sm font-bold uppercase tracking-tight">
                Step {slotIndex != null ? slotIndex + 1 : "?"}{totalSteps ? ` of ${totalSteps}` : ""} · {slotLabel}
              </p>
            )}
          </div>
          <input
            autoFocus
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search products…"
            className="flex-1 min-w-[200px] bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
            data-testid="admin-picker-search"
          />
          <span className="text-white/70 text-xs">{allProducts.length} synced · {selected.size} selected</span>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              data-testid="admin-picker-refresh"
              title="Re-pull the full product list live from Printful"
              className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-2 border border-white/20 hover:border-[#FF1B91] disabled:opacity-40"
            >
              <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
              {loading ? "Syncing…" : "Refresh"}
            </button>
          )}
          <button type="button" onClick={onCancel} data-testid="admin-picker-cancel"
            className="text-white/60 hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-2 border border-white/15">Cancel</button>
          <button type="button" onClick={() => onSave(Array.from(selected), false)} data-testid="admin-picker-save"
            className="bg-[#FF1B91] hover:bg-[#FF3FA3] text-white text-[10px] font-bold uppercase tracking-[0.25em] px-4 py-2">Save &amp; close</button>
          {hasNext && (
            <button type="button" onClick={() => onSave(Array.from(selected), true)} data-testid="admin-picker-save-next"
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-[0.25em] px-4 py-2">Save &amp; next step →</button>
          )}
        </header>
        <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filtered.map((p) => {
            const isOn = selected.has(Number(p.id));
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(Number(p.id))}
                data-testid={`admin-picker-toggle-${p.id}`}
                className={`text-left border-2 bg-[#0F0F0F] overflow-hidden transition-colors ${isOn ? "border-[#FF1B91] shadow-[0_0_0_2px_#FF1B91]" : "border-white/15 hover:border-[#FF1B91]/60"}`}
              >
                <div className="aspect-square bg-[#141414] overflow-hidden relative">
                  {p.thumbnail_url ? <img src={p.thumbnail_url} alt={p.name} loading="lazy" className="w-full h-full object-cover" /> : null}
                  {isOn && <span className="absolute top-1 right-1 bg-[#FF1B91] text-white text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5">✓ Picked</span>}
                  {p.is_ignored_product && <span className="absolute bottom-1 left-1 bg-red-600 text-white text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5">Ignored</span>}
                  {!p.is_ignored_product && p.ignored_variant_count > 0 && (
                    <span className="absolute bottom-1 left-1 bg-yellow-500 text-black text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5" title="Some variants are marked ignored in Printful">{p.ignored_variant_count} ignored var.</span>
                  )}
                </div>
                <div className="p-2">
                  <p className="text-white text-[11px] font-bold leading-tight line-clamp-2 min-h-[2.5rem]">{p.name}</p>
                  {p.price_from != null && <p className="text-white/50 text-[10px] mt-0.5">${Math.round(p.price_from)}{p.price_to && p.price_to !== p.price_from ? `–$${Math.round(p.price_to)}` : ""}</p>}
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && <p className="col-span-full text-white/40 italic text-sm">No products match.</p>}
        </div>
      </div>
    </div>
  );
}
